// Ashima Arts simplex 3D noise — classic implementation
const NOISE_GLSL = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`;

export const vertexShader = /* glsl */ `
uniform float uTime;

varying vec3 vNormal;
varying vec3 vWorldPos;
varying float vDisplace;

${NOISE_GLSL}

void main() {
  vec3 pos = position;

  // Domain warp: offset sample coords by a lower-frequency noise
  float warp = snoise(pos * 0.6 + uTime * 0.10) * 0.20;
  vec3 warpedPos = pos + normal * warp;

  // Two-octave displacement: large slow waves + gentle secondary swell
  float d1 = snoise(warpedPos * 1.1 + uTime * 0.18);
  float d2 = snoise(warpedPos * 2.0 - uTime * 0.30);
  float displacement = d1 * 0.13 + d2 * 0.025;

  vDisplace = displacement;
  vNormal   = normalize(normalMatrix * normal);

  vec3 displaced = pos + normal * displacement;
  vec4 worldPos  = modelMatrix * vec4(displaced, 1.0);
  vWorldPos      = worldPos.xyz;

  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

export const fragmentShader = /* glsl */ `
uniform float uTime;
uniform vec3  uLightDir;    // normalized, mouse-driven
uniform vec3  uCameraPos;

varying vec3  vNormal;
varying vec3  vWorldPos;
varying float vDisplace;

// Map a 0-1 value to a luminescent blue color
vec3 lavaColor(float t) {
  // three control points: valley → mid → peak
  vec3 valley = vec3(0.00, 0.02, 0.12);   // deep navy
  vec3 mid    = vec3(0.02, 0.20, 0.80);   // electric blue
  vec3 peak   = vec3(0.15, 0.50, 0.95);   // bright blue (not white)

  if (t < 0.5) return mix(valley, mid, t * 2.0);
  return mix(mid, peak, (t - 0.5) * 2.0);
}

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(uCameraPos - vWorldPos);
  vec3 L = normalize(uLightDir);

  // Fresnel — power 3 gives a nice wide rim
  float fresnel = pow(1.0 - max(dot(V, N), 0.0), 3.0);

  // Remap displacement from roughly [-0.18, 0.18] to [0, 1]
  float t = clamp((vDisplace + 0.18) / 0.36, 0.0, 1.0);
  vec3 baseColor = lavaColor(t);

  // Specular highlight — tinted blue so it doesn't whiten the surface
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(N, H), 0.0), 64.0) * 0.7;

  // Rim glow — HDR so bloom picks it up
  vec3 rimColor = vec3(0.05, 0.30, 1.0) * 1.6 * fresnel;

  // Combine: base emissive + specular highlight + rim
  vec3 color = baseColor * 1.1 + vec3(0.1, 0.3, 1.0) * spec + rimColor;

  // Animated subsurface vein: thin bright streaks in bright peaks
  float veinMask = smoothstep(0.68, 0.82, t);
  color += vec3(0.3, 0.65, 1.0) * veinMask * 0.5;

  gl_FragColor = vec4(color, 1.0);
}
`;
